import React, { useEffect, useMemo, useState } from 'react';
import logo from './img/logo-comptelio.png';

const PAGE_TITLE = 'Checklist Visibilité — 10 points pour experts-comptables | Comptelio';
const PAGE_DESCRIPTION = "10 points qui font qu'un prospect vous choisit (ou pas) en ligne. Checklist gratuite pour cabinets d'experts-comptables.";
const STORAGE_KEY = 'clotere-checklist-visibilite';

const CATEGORIES = [
  {
    tag: 'Visible',
    items: [
      {
        id: 'p1',
        title: "Vous apparaissez en 1ère page Google sur « expert-comptable + [votre ville] »",
        text: "Si vous n'y êtes pas, vos confrères y sont — et récupèrent la recherche.",
      },
      {
        id: 'p2',
        title: 'Votre fiche Google Business Profile est complète et bien classée dans le pack local',
        text: "Horaires, photos, catégorie, spécialités : c'est souvent la 1ère chose vue avant le site.",
      },
      {
        id: 'p3',
        title: 'Votre site donne envie de prendre rendez-vous en moins de 10 secondes',
        text: "Message clair, preuve de sérieux, bouton d'action visible dès l'arrivée — pas seulement « expertise et proximité ».",
      },
    ],
  },
  {
    tag: 'Vérifié',
    items: [
      {
        id: 'p4',
        title: 'Vous avez au moins 15 avis Google récents, et vous y répondez',
        text: 'Un cabinet sans avis récents inspire moins confiance que celui installé depuis 20 ans mais silencieux en ligne.',
      },
      {
        id: 'p5',
        title: 'Votre présence LinkedIn montre une activité récente, pas une page vitrine figée',
        text: 'Un dirigeant qui vous cherche avant de vous appeler doit voir que le cabinet est vivant.',
      },
      {
        id: 'p6',
        title: 'Un prospect voit en 2 clics vos spécialités, votre équipe et une fourchette de tarifs',
        text: "L'opacité totale sur le prix pousse à comparer ailleurs.",
      },
      {
        id: 'p7',
        title: 'Vous avez des cas clients concrets, pas seulement des mots-clés génériques',
        text: '« Nous accompagnons +80 TPE du BTP » convainc plus que « rigueur et proximité ».',
      },
    ],
  },
  {
    tag: 'Volume',
    items: [
      {
        id: 'p8',
        title: 'Un prospect peut réserver un rendez-vous en ligne, sans appeler ni attendre un rappel',
        text: "Beaucoup abandonnent avant même de décrocher le téléphone si c'est la seule option.",
      },
      {
        id: 'p9',
        title: 'Vous savez précisément combien de nouveaux dossiers viennent d’internet chaque mois',
        text: "Sans chiffre, impossible de savoir si votre com' rapporte.",
      },
      {
        id: 'p10',
        title: "Vous relancez automatiquement les demandes de contact qui n'ont pas abouti",
        text: 'Un formulaire rempli un vendredi soir et jamais relancé, c’est un dossier perdu pour rien.',
      },
    ],
  },
];

const TOTAL_POINTS = CATEGORIES.reduce((sum, category) => sum + category.items.length, 0);

const SCORE_RANGES = [
  { key: 'high', min: 8, max: 10, label: '8–10', text: 'vous êtes bien positionné, il reste des détails à affiner.' },
  { key: 'mid', min: 5, max: 7, label: '5–7', text: 'il y a des trous dans la raquette qui coûtent des dossiers chaque mois.' },
  { key: 'low', min: 0, max: 4, label: '0–4', text: 'vous perdez probablement des prospects sans même le savoir.' },
];

function loadChecked() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveChecked(checked) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(checked));
  } catch {
    // stockage indisponible (navigation privée, etc.) — on continue sans persister
  }
}

function setMeta(attr, key, content) {
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  const previous = el.getAttribute('content');
  el.setAttribute('content', content);
  return previous;
}

const ChecklistVisibilite = () => {
  const [checked, setChecked] = useState(() => (typeof window !== 'undefined' ? loadChecked() : {}));

  const toggle = (id) => {
    setChecked((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      saveChecked(next);
      return next;
    });
  };

  const reset = () => {
    setChecked({});
    saveChecked({});
  };

  const score = useMemo(
    () => Object.values(checked).filter(Boolean).length,
    [checked]
  );
  const activeRange = SCORE_RANGES.find((range) => score >= range.min && score <= range.max);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = PAGE_TITLE;

    const ogImage = `${window.location.origin}/images/checklist-cover.png`;
    const prevDescription = setMeta('name', 'description', PAGE_DESCRIPTION);
    const prevOgTitle = setMeta('property', 'og:title', PAGE_TITLE);
    const prevOgDescription = setMeta('property', 'og:description', PAGE_DESCRIPTION);
    const prevOgImage = setMeta('property', 'og:image', ogImage);

    return () => {
      document.title = previousTitle;
      setMeta('name', 'description', prevDescription ?? '');
      setMeta('property', 'og:title', prevOgTitle ?? '');
      setMeta('property', 'og:description', prevOgDescription ?? '');
      setMeta('property', 'og:image', prevOgImage ?? '');
    };
  }, []);

  let pointNumber = 0;

  return (
    <div className="checklist-visibilite">
      <style>{`
        .checklist-visibilite{
          --bg:#f7f5f1;
          --surface:#ffffff;
          --ink:#1c1b19;
          --ink-soft:#55524b;
          --border:#e6e1d8;
          --accent:#0f5132;
          --accent-soft:#e7f2ec;
          --accent-2:#b8712f;
          --accent-2-soft:#f8ecdd;
          background:var(--bg);
          color:var(--ink);
          font-family:-apple-system,BlinkMacSystemFont,'Inter',system-ui,sans-serif;
          line-height:1.55;
        }
        .checklist-visibilite *{box-sizing:border-box;}
        .checklist-visibilite .wrap{
          max-width:760px;
          margin-inline:auto;
          padding:clamp(32px,6vw,72px) clamp(18px,4vw,24px);
        }
        .checklist-visibilite header{
          position:sticky;top:0;z-index:40;
          background:color-mix(in srgb, var(--bg) 88%, transparent);
          backdrop-filter:blur(10px);
          border-bottom:1px solid var(--border);
        }
        .checklist-visibilite .nav{
          display:flex;align-items:center;justify-content:space-between;
          max-width:760px;margin-inline:auto;
          padding:16px clamp(18px,4vw,24px);
        }
        .checklist-visibilite .brand{
          display:flex;align-items:center;gap:9px;
          font-weight:800;font-size:17px;letter-spacing:-.01em;
          color:var(--ink);text-decoration:none;
        }
        .checklist-visibilite .brand-mark{
          width:28px;height:28px;border-radius:8px;flex:none;display:block;
        }
        .checklist-visibilite .brand-sub{font-weight:500;font-size:12px;color:var(--ink-soft);margin-left:2px;}
        .checklist-visibilite .nav-cta{
          display:inline-block;background:var(--accent);color:#fff;
          font-weight:700;font-size:13.5px;
          padding:10px 18px;border-radius:10px;text-decoration:none;flex:none;
        }
        .checklist-visibilite .nav-cta:hover{opacity:.92;}
        @media (max-width:600px){
          .checklist-visibilite .nav{padding:12px 16px;}
          .checklist-visibilite .brand-sub{display:none;}
          .checklist-visibilite .nav-cta{padding:9px 14px;font-size:13px;}
        }
        .checklist-visibilite .eyebrow{
          display:inline-flex;align-items:center;
          font-size:13px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;
          color:var(--accent);background:var(--accent-soft);
          padding:6px 14px;border-radius:999px;margin-bottom:20px;
        }
        .checklist-visibilite h1{
          margin:0 0 16px;
          font-size:clamp(28px,4.2vw,40px);
          font-weight:700;
          letter-spacing:-.015em;
          line-height:1.15;
          color:var(--ink);
        }
        .checklist-visibilite .subtitle{
          margin:0 0 32px;
          font-size:clamp(16px,2vw,18px);
          color:var(--ink-soft);
          max-width:60ch;
        }
        .checklist-visibilite .intro{
          background:var(--surface);
          border:1px solid var(--border);
          border-radius:18px;
          padding:22px 24px;
          margin-bottom:40px;
          color:var(--ink-soft);
          font-size:15.5px;
        }
        .checklist-visibilite .intro strong{color:var(--ink);}
        .checklist-visibilite .category{margin-bottom:36px;}
        .checklist-visibilite .category-tag{
          display:inline-flex;align-items:center;
          font-size:12.5px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;
          color:var(--accent);background:var(--accent-soft);
          padding:6px 14px;border-radius:999px;
          margin-bottom:16px;
        }
        .checklist-visibilite .point-hint{
          display:flex;align-items:center;gap:8px;
          font-size:13px;font-weight:600;color:var(--ink-soft);
          margin-bottom:14px;
        }
        .checklist-visibilite .point-hint svg{flex:none;color:var(--accent);}
        .checklist-visibilite .point-list{list-style:none;margin:0;padding:0;display:flex;flex-direction:column;gap:6px;}
        .checklist-visibilite .point-label{
          display:flex;gap:14px;align-items:flex-start;
          cursor:pointer;
          padding:10px;margin:-10px;
          border-radius:12px;
          transition:background .15s ease;
        }
        .checklist-visibilite .point-label:hover{background:var(--surface);}
        .checklist-visibilite .point-label:hover .point-check{
          border-color:var(--accent);
          box-shadow:0 0 0 3px var(--accent-soft);
        }
        .checklist-visibilite .sr-only-checkbox{
          position:absolute;width:1px;height:1px;padding:0;margin:-1px;
          overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0;
        }
        .checklist-visibilite .sr-only-checkbox:focus-visible + .point-check{
          outline:2px solid var(--accent);outline-offset:2px;
        }
        .checklist-visibilite .point-check{
          flex:none;
          width:28px;height:28px;
          border-radius:8px;
          background:var(--surface);
          border:2px solid var(--border);
          color:var(--ink-soft);
          display:flex;align-items:center;justify-content:center;
          transition:background .15s ease,color .15s ease,border-color .15s ease,box-shadow .15s ease,transform .1s ease;
        }
        .checklist-visibilite .point-check svg{width:16px;height:16px;}
        .checklist-visibilite .point-label:active .point-check{transform:scale(.92);}
        .checklist-visibilite .point-check.is-checked{
          background:var(--accent);
          border-color:var(--accent);
          color:#fff;
        }
        .checklist-visibilite .point-number{
          flex:none;
          padding-top:4px;
          font-weight:700;
          font-size:13px;
          color:var(--ink-soft);
          min-width:16px;
        }
        .checklist-visibilite .point-body{display:flex;flex-direction:column;}
        .checklist-visibilite .point-title{
          margin:0 0 4px;
          font-weight:600;
          color:var(--ink);
          font-size:15.5px;
          transition:color .15s ease;
        }
        .checklist-visibilite .point-text{
          margin:0;
          color:var(--ink-soft);
          font-size:14.5px;
        }
        .checklist-visibilite .point-label.is-checked .point-title{
          color:var(--ink-soft);
          text-decoration:line-through;
          text-decoration-color:var(--border);
        }
        .checklist-visibilite .score-box{
          background:var(--accent-2-soft);
          border:1px solid var(--border);
          border-radius:18px;
          padding:24px;
          margin:40px 0;
        }
        .checklist-visibilite .score-head{
          display:flex;align-items:baseline;justify-content:space-between;gap:12px;
          margin-bottom:14px;
        }
        .checklist-visibilite .score-box h2{
          margin:0;
          font-size:16px;
          font-weight:700;
          color:var(--ink);
        }
        .checklist-visibilite .score-count{
          font-size:13.5px;font-weight:700;color:var(--accent-2);flex:none;
        }
        .checklist-visibilite .score-bar{
          height:8px;border-radius:999px;
          background:color-mix(in srgb, var(--accent-2) 18%, var(--surface));
          overflow:hidden;margin-bottom:18px;
        }
        .checklist-visibilite .score-bar-fill{
          height:100%;background:var(--accent-2);border-radius:999px;
          transition:width .25s ease;
        }
        .checklist-visibilite .score-row{
          display:flex;gap:10px;align-items:baseline;
          font-size:14.5px;color:var(--ink-soft);
          padding:6px 10px;border-radius:10px;
          transition:background .15s ease,color .15s ease;
        }
        .checklist-visibilite .score-row.is-active{
          background:var(--surface);
          color:var(--ink);
          font-weight:600;
        }
        .checklist-visibilite .score-range{
          flex:none;font-weight:700;color:var(--accent-2);min-width:56px;
        }
        .checklist-visibilite .score-reset{
          margin-top:12px;
          background:none;border:none;padding:0;
          font-size:13px;font-weight:600;color:var(--accent-2);
          text-decoration:underline;cursor:pointer;
        }
        .checklist-visibilite .cta-box{
          background:var(--surface);
          border:1px solid var(--border);
          border-radius:18px;
          padding:32px;
          text-align:center;
          margin-bottom:32px;
        }
        .checklist-visibilite .cta-box h2{
          margin:0 0 12px;
          font-size:clamp(20px,2.6vw,24px);
          font-weight:700;
          color:var(--ink);
        }
        .checklist-visibilite .cta-box p{
          margin:0 0 22px;
          color:var(--ink-soft);
          font-size:15.5px;
          max-width:46ch;
          margin-inline:auto;
        }
        .checklist-visibilite .cta-button{
          display:inline-block;
          background:var(--accent);
          color:#fff;
          font-weight:700;
          font-size:15.5px;
          padding:14px 28px;
          border-radius:12px;
          text-decoration:none;
        }
        .checklist-visibilite .cta-button:hover{opacity:.92;}
        .checklist-visibilite .cta-subtext{
          margin-top:20px;
          font-size:13.5px;
          color:var(--ink-soft);
        }
        .checklist-visibilite .footer{
          text-align:center;
          font-size:13px;
          color:var(--ink-soft);
          padding-top:16px;
        }

        @media (max-width:600px){
          .checklist-visibilite .wrap{padding:28px 16px;}
          .checklist-visibilite h1{font-size:24px;}
          .checklist-visibilite .subtitle{font-size:15px;}
          .checklist-visibilite .intro{padding:18px;font-size:14.5px;}
          .checklist-visibilite .point-label{gap:10px;padding:8px;margin:-8px;}
          .checklist-visibilite .point-check{width:24px;height:24px;}
          .checklist-visibilite .point-check svg{width:14px;height:14px;}
          .checklist-visibilite .score-head{flex-direction:column;align-items:flex-start;gap:4px;}
          .checklist-visibilite .point-title{font-size:14.5px;}
          .checklist-visibilite .point-text{font-size:13.5px;}
          .checklist-visibilite .cta-box{padding:24px 18px;}
          .checklist-visibilite .cta-button{width:100%;padding:14px 20px;}
        }
      `}</style>

      <header>
        <div className="nav">
          <a href="/" className="brand">
            <img src={logo} alt="Comptelio" className="brand-mark" />
            Comptelio
          </a>
          <a
            href="https://calendar.app.google/sjzvsBcq6yTLRReP6"
            className="nav-cta"
            target="_blank"
            rel="noopener noreferrer"
          >
            Réserver une démo
          </a>
        </div>
      </header>

      <div className="wrap">
        <div className="eyebrow">Comptelio · Experts-Comptables</div>

        <h1>Les 10 points qui font qu'un prospect vous choisit (ou pas) en ligne</h1>
        <p className="subtitle">
          Une checklist en 3 étapes — VISIBLE, VÉRIFIÉ, VOLUME — pour savoir exactement où vous perdez des dossiers avant même le premier échange.
        </p>

        <div className="intro">
          Beaucoup de cabinets ont une <strong>belle image</strong> — un joli site, un logo soigné — mais aucun moyen de savoir combien de dossiers ça leur ramène vraiment. Cette checklist ne parle pas de design : elle parle de <strong>ce qui fait qu'un prospect vous contacte, vous ou le cabinet d'à côté</strong>. Cochez chaque point qui est déjà vrai chez vous.
        </div>

        <div className="point-hint">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 11l3 3L22 4" />
            <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
          </svg>
          Cocher les cases : cliquez sur chaque point qui est déjà vrai chez vous — votre score se met à jour automatiquement.
        </div>

        {CATEGORIES.map((category) => (
          <div className="category" key={category.tag}>
            <div className="category-tag">{category.tag}</div>
            <ul className="point-list">
              {category.items.map((item) => {
                pointNumber += 1;
                const isChecked = !!checked[item.id];
                return (
                  <li className="point" key={item.id}>
                    <label className={`point-label${isChecked ? ' is-checked' : ''}`}>
                      <input
                        type="checkbox"
                        className="sr-only-checkbox"
                        checked={isChecked}
                        onChange={() => toggle(item.id)}
                      />
                      <span className={`point-check${isChecked ? ' is-checked' : ''}`} aria-hidden="true">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M4 12l5 5L20 6" />
                        </svg>
                      </span>
                      <span className="point-number" aria-hidden="true">{pointNumber}</span>
                      <span className="point-body">
                        <span className="point-title">{item.title}</span>
                        <span className="point-text">{item.text}</span>
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}

        <div className="score-box">
          <div className="score-head">
            <h2>Votre score</h2>
            <span className="score-count">{score} / {TOTAL_POINTS} points cochés</span>
          </div>
          <div className="score-bar" role="progressbar" aria-valuenow={score} aria-valuemin={0} aria-valuemax={TOTAL_POINTS}>
            <div className="score-bar-fill" style={{ width: `${(score / TOTAL_POINTS) * 100}%` }} />
          </div>
          {SCORE_RANGES.map((range) => (
            <div className={`score-row${activeRange?.key === range.key ? ' is-active' : ''}`} key={range.key}>
              <span className="score-range">{range.label}</span>
              <span>{range.text}</span>
            </div>
          ))}
          {score > 0 && (
            <button type="button" className="score-reset" onClick={reset}>
              Réinitialiser la checklist
            </button>
          )}
        </div>

        <div className="cta-box">
          <h2>Envie de savoir précisément où vous perdez des dossiers ?</h2>
          <p>15 minutes, gratuites et sans engagement : on regarde vos 10 points ensemble et on identifie les 2-3 qui vous coûtent le plus.</p>
          <a className="cta-button" href="https://comptelio.fr/#contact">Réserver mon audit gratuit</a>
          <br/><br/> <p className="cta-subtext">Membre du groupe LinkedIn ? Cette checklist est faite pour vous.</p>
        </div>

        <div className="footer">
          Comptelio — site, prise de rendez-vous en ligne et communication pour experts-comptables · comptelio.fr
        </div>
      </div>
    </div>
  );
};

export default ChecklistVisibilite;
